import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BaseQuoteToolModeService, QuoteToolStrategyService } from '@lx/quote-tool/shared/services';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { RouteIds } from '../../types';
import { NavigationService } from '../navigation.service';
import {
  DefaultModeStrategyService,
  JuniorCampModeStrategyService,
  OnlineCourseModeStrategyService,
  PaymentModeStrategyService,
  StudyAndStayModeStrategyService,
} from './strategies';
import { AcademicYearAbroadDeepLinkStrategyService } from './strategies/academic-year-abroad-deep-link-strategy.service';
import { ActiveFlowModeStrategyService } from './strategies/active-flow-strategy.service';

@Injectable({
  providedIn: 'root',
})
export class QuoteToolLiteModeService extends BaseQuoteToolModeService<RouteIds> {
  private readonly loaded$ = new BehaviorSubject<boolean>(false);

  private readonly navigationService = inject(NavigationService);
  private readonly defaultModeStrategy = inject(DefaultModeStrategyService);
  private readonly juniorCampModeStrategy = inject(JuniorCampModeStrategyService);
  private readonly academicYearAbroadDeepLinkStrategyService = inject(AcademicYearAbroadDeepLinkStrategyService);
  private readonly paymentModeStrategy = inject(PaymentModeStrategyService);
  private readonly onlineCourseModeStrategy = inject(OnlineCourseModeStrategyService);
  private readonly activeFlowModeStrategyService = inject(ActiveFlowModeStrategyService);
  private readonly studyAndStayModeStrategy = inject(StudyAndStayModeStrategyService);

  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    super();
    this.strategies = {
      default: this.defaultModeStrategy,
      'junior-camp': this.juniorCampModeStrategy,
      payment: this.paymentModeStrategy,
      'online-course': this.onlineCourseModeStrategy,
      'study-and-stay': this.studyAndStayModeStrategy,
      'academic-year-abroad': this.academicYearAbroadDeepLinkStrategyService,
      adult: this.activeFlowModeStrategyService,
      junior: this.activeFlowModeStrategyService,
      aya: this.activeFlowModeStrategyService,
      online: this.activeFlowModeStrategyService,
    };
  }

  get loaded(): boolean {
    return this.loaded$.getValue();
  }

  /**
   * Load the data for the selected strategy. If the loading fails, it will
   * call to loadData$ method of the default strategy.
   */
  loadData$(): Observable<boolean> {
    return this.getStrategy()
      .loadData$()
      .pipe(
        switchMap((loaded) => {
          if (!loaded) {
            this.logger.error(`Error loading data for quote tool mode: ${this.hostDataReader.getQuoteToolMode()}. Loading default data.`);
            this.hostDataReader.resetQueryParameters(); // reset the query parameters when facing an error that makes that the data was not loaded
            return this.strategies.default.loadData$();
          }
          return of(loaded);
        }),
        // Set loaded data state on success
        switchMap((loaded) => {
          if (loaded) {
            this.loaded$.next(true);
          }
          return of(loaded);
        }),
        takeUntilDestroyed(this.destroyRef),
      );
  }

  navigate(): Observable<boolean> {
    // Choose the strategy
    const strategy: QuoteToolStrategyService<RouteIds> = this.getStrategy();

    // Do the navigation
    this.doNavigation(strategy);

    return this.navigationCompleted$.asObservable();
  }

  protected doNavigation(strategy: QuoteToolStrategyService<RouteIds>): void {
    (strategy.getStepToNavigate() as Observable<RouteIds>).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((routeId) => {
      this.navigationService.navigateToStep(routeId);
    });
  }

  protected navigateToDefault(): void {
    this.navigationService.navigateToFirstStep();
  }
}
